// Seamless announcement bar rotation with state persistence
(function () {
  const STORAGE_KEY = 'announcementBarState';
  const ANIMATION_DURATION = 250; // 250ms animation

  class AnnouncementBarManager {
    constructor() {
      this.bar = document.querySelector(
        '.announcement-bar[data-rotate="true"]'
      );
      if (!this.bar) return;

      this.messages = this.bar.querySelectorAll('.announcement-bar__message');
      if (this.messages.length <= 1) return;

      this.delay = parseInt(this.bar.dataset.delay, 10) * 1000 || 5000;
      this.state = this.loadState();
      this.intervalId = null;
      this.resumeTimeoutId = null;
      this.cleanupTimeoutId = null;
      this.transitioning = false;

      // CRITICAL: Set correct state IMMEDIATELY to prevent flicker
      this.preventInitialWrongState();

      this.init();
    }

    // Load persisted state and calculate current position
    loadState() {
      try {
        const saved = localStorage.getItem(STORAGE_KEY);
        if (!saved) return null;

        const state = JSON.parse(saved);
        const timePassed = Date.now() - state.lastTransition;
        const intervalsPassed = Math.floor(timePassed / state.interval);

        // Calculate new index based on time passed
        const newIndex =
          (state.currentIndex + intervalsPassed) % this.messages.length;

        return {
          currentIndex: newIndex,
          lastTransition: state.lastTransition,
          interval: state.interval,
          nextTransition:
            state.lastTransition + (intervalsPassed + 1) * state.interval,
        };
      } catch (e) {
        // Reset on any error
        return null;
      }
    }

    // Save current state to localStorage
    saveState() {
      try {
        const state = {
          currentIndex: this.state.currentIndex,
          lastTransition: this.state.lastTransition,
          interval: this.delay,
        };
        localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
      } catch (e) {
        // Silently fail if localStorage is unavailable
      }
    }

    // Initialize the announcement bar
    init() {
      // Set initial visual state immediately (before DOM is fully ready)
      this.setInitialVisualState();

      // Start the rotation timer
      this.startRotation();

      // Clean up old state periodically
      this.scheduleCleanup();

      // Desktop previous / next arrows (rendered by sections/announcement-bar.liquid, shown from 990 px by rd-chrome.css)
      this.bar.querySelectorAll('[data-announcement-step]').forEach((button) => {
        button.addEventListener('click', () => this.step(parseInt(button.dataset.announcementStep, 10)));
      });
    }

    // Manual step (+1 next, -1 previous): same animation and saved state as the timer, then the auto-rotation starts over
    // from this click so the message is not replaced a moment later
    step(direction) {
      if (this.transitioning) return;
      clearInterval(this.intervalId);
      clearTimeout(this.resumeTimeoutId);
      this.performTransition(direction);
      this.scheduleNextRotation();
    }

    // CSS-FIRST solution: Bypass CSS transitions entirely
    preventInitialWrongState() {
      if (!this.state) return;

      const correctIndex = this.state.currentIndex;

      // Apply styles DIRECTLY to bypass CSS transition system
      this.messages.forEach((message, index) => {
        if (index === correctIndex) {
          // Force immediate correct visibility
          message.style.display = 'block';
          message.style.opacity = '1';
          message.style.transform = 'translateY(0)';
          message.style.position = 'relative';
          message.style.zIndex = '2';

          // Set data attribute for CSS fallback
          message.setAttribute('data-correct-announcement', 'true');
          message.classList.add('active');
        } else {
          // Force immediate hiding
          message.style.display = 'none';
          message.style.opacity = '0';
          message.style.transform = 'translateY(-100%)';
          message.style.position = 'absolute';
          message.style.zIndex = '1';

          // Remove data attribute and active class
          message.removeAttribute('data-correct-announcement');
          message.classList.remove('active');
        }
      });
    }

    // Set the correct announcement as active immediately (fallback)
    setInitialVisualState() {
      const initialIndex = this.state ? this.state.currentIndex : 0;

      this.messages.forEach((message, index) => {
        message.classList.toggle('active', index === initialIndex);
      });
    }

    // Start the rotation cycle
    startRotation() {
      if (this.state) {
        // Resume from saved position with precise timing
        const now = Date.now();
        const timeUntilNext = Math.max(0, this.state.nextTransition - now);

        // Schedule next transition at the exact right time
        this.resumeTimeoutId = setTimeout(() => {
          this.performTransition();
          this.scheduleNextRotation();
        }, timeUntilNext);
      } else {
        // Start fresh rotation cycle
        this.state = {
          currentIndex: 0,
          lastTransition: Date.now(),
          interval: this.delay,
          nextTransition: Date.now() + this.delay,
        };
        this.scheduleNextRotation();
      }
    }

    // Schedule the next rotation
    scheduleNextRotation() {
      const pageWidth = this.bar.querySelector('.page-width');
      if (pageWidth) pageWidth.setAttribute('aria-live', 'off');

      this.intervalId = setInterval(() => {
        this.performTransition();
      }, this.delay);
    }

    // Perform the transition animation (direction 1: next message slides up in, -1: previous slides down in)
    performTransition(direction = 1) {
      if (this.transitioning) return;
      this.transitioning = true;

      const currentIndex = this.state.currentIndex;
      const nextIndex = (currentIndex + direction + this.messages.length) % this.messages.length;
      const current = this.messages[currentIndex];
      const next = this.messages[nextIndex];

      if (!current || !next) {
        this.transitioning = false;
        return;
      }

      // Set next message to initial entering position
      next.style.display = 'block';
      next.style.position = 'absolute';
      next.style.zIndex = '1';
      next.style.transform = `translateY(${direction * 100}%)`;
      next.style.opacity = '0';
      next.classList.add('entering');

      // Force reflow so the browser registers the initial state before animating
      next.offsetHeight;

      // Now animate to final positions
      next.style.transform = 'translateY(0)';
      next.style.opacity = '1';

      current.style.transform = `translateY(${direction * -100}%)`;
      current.style.opacity = '0';
      current.style.zIndex = '3';
      current.classList.add('exiting');

      // Update state immediately
      this.state.currentIndex = nextIndex;
      this.state.lastTransition = Date.now();
      this.state.nextTransition = Date.now() + this.delay;

      // After animation completes, clean up and finalize
      setTimeout(() => {
        current.style.display = 'none';
        current.style.transform = 'translateY(-100%)';
        current.style.opacity = '0';
        current.style.position = 'absolute';
        current.style.zIndex = '1';
        current.classList.remove('active', 'exiting');
        current.removeAttribute('data-correct-announcement');

        next.style.position = 'relative';
        next.style.zIndex = '2';
        next.classList.remove('entering');
        next.classList.add('active');
        next.setAttribute('data-correct-announcement', 'true');

        // Update wrapper color scheme to match active message
        const colorClass = next.className.match(/color-[\w-]+/);
        if (colorClass && this.bar) {
          this.bar.className = this.bar.className.replace(/color-[\w-]+/g, '');
          this.bar.classList.add(colorClass[0]);
        }

        this.transitioning = false;
        this.saveState();
      }, ANIMATION_DURATION);
    }

    // Clean up old state to prevent storage bloat
    scheduleCleanup() {
      // Clean up state older than 24 hours
      this.cleanupTimeoutId = setTimeout(() => {
        try {
          const saved = localStorage.getItem(STORAGE_KEY);
          if (saved) {
            const state = JSON.parse(saved);
            const age = Date.now() - state.lastTransition;
            if (age > 24 * 60 * 60 * 1000) {
              // 24 hours
              localStorage.removeItem(STORAGE_KEY);
            }
          }
        } catch (e) {
          // Ignore cleanup errors
        }
      }, 60 * 60 * 1000); // Check after 1 hour
    }

    // Clean up when leaving the page
    destroy() {
      if (this.intervalId) clearInterval(this.intervalId);
      if (this.resumeTimeoutId) clearTimeout(this.resumeTimeoutId);
      if (this.cleanupTimeoutId) clearTimeout(this.cleanupTimeoutId);

      const pageWidth = this.bar && this.bar.querySelector('.page-width');
      if (pageWidth) pageWidth.setAttribute('aria-live', 'polite');
    }
  }

  // Initialize when DOM is ready (script is deferred)
  const manager = new AnnouncementBarManager();

  window.addEventListener('beforeunload', () => {
    if (manager) {
      manager.saveState();
      manager.destroy();
    }
  });
})();
